import * as React from "react";
import { useState, useEffect } from "react";
import Button from "../Core/Button";
import Price, { MoneyFormat } from "./Price";
import SwatchesGroup from "./SwatchesGroup";
import ColourVariations from "./ColourVariations";
import ShopifyProduct from "../../interface/ShopifyProduct";
import ProductColourVariation from "../../interface/ProductColourVariation";
import { ProductSectionSettings } from "../../interface/ProductSectionSettings";
import { addToCart } from "./addToCart";
import Afterpay from "../Afterpay/Afterpay";
import GiftCardForm from "./GiftCardForm";
import { GiftCardData } from "@app/interface/ProductGiftCard";
import Braze from "../../libraries/Braze";

const CONST_ADDING_TO_BAG = "Adding to Bag...";
const CONST_ADD_TO_BAG = "Add to Bag";
const CONST_BAG_SUCCESS = "Successfully added";

interface ProductFormProps {
  product: ShopifyProduct;
  colourVariations: ProductColourVariation[];
  productSectionSettings: ProductSectionSettings;
  moneyFormat: MoneyFormat;
  afterpayEnabled: boolean;
}

interface SwatchesProps {
  index: number;
  option: string;
}

const ProductForm = (props: ProductFormProps) => {
  const { product, moneyFormat, colourVariations, productSectionSettings, afterpayEnabled } = props;
  //Check for the first available variant to pre-select
  const availableVariant =
    product.variants.find((variant) => {
      return variant.available;
    }) || product.variants[0];
  const [currentVariant, setCurrentVariant] = useState(availableVariant ? availableVariant : null);
  const [option1, setOption1] = useState(availableVariant.options[0] ? availableVariant.options[0] : null);
  const [option2, setOption2] = useState(availableVariant.options[1] ? availableVariant.options[1] : null);
  const [option3, setOption3] = useState(availableVariant.options[2] ? availableVariant.options[2] : null);
  const [optionChanged, setOptionChanged] = useState(0);

  const [addingToCart, setAddingToCart] = useState(false);
  const [addingSuccess, setAddingSuccess] = useState(false);
  const [addToCartButton, setAddToCartButton] = useState(CONST_ADD_TO_BAG);
  const [giftCardData, setGiftCardData] = useState<GiftCardData>({
    recipientName: undefined,
    senderName: undefined,
    recipientEmail: undefined,
    message: undefined,
  });

  const isGiftCard = product.tags.includes("gift-card");
  const invalidGiftCardData =
    isGiftCard && (!giftCardData.recipientName || !giftCardData.recipientEmail || !giftCardData.senderName);

  const onAddToBag = () => {
    if (currentVariant) {
      setAddingToCart(true);
      const attributes = giftCardData.recipientName
        ? {
            _gift_sender_name: giftCardData.senderName,
            _gift_recipient_name: giftCardData.recipientName,
            _gift_recipient_email: giftCardData.recipientEmail,
            _gift_recipient_message: giftCardData.message,
            _rand: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
          }
        : undefined;
      addToCart(
        currentVariant,
        1,
        attributes,
        () => {
          setAddingToCart(false), setAddingSuccess(true);
        },
        () => {
          setAddingToCart(false), setAddingSuccess(false);
        },
      );
    }
  };

  useEffect(() => {
    if (addingToCart) {
      setAddToCartButton(CONST_ADDING_TO_BAG);
    } else {
      setAddToCartButton(CONST_ADD_TO_BAG);
    }
    if (addingSuccess) {
      setAddToCartButton(CONST_BAG_SUCCESS);
      setTimeout(() => {
        setAddToCartButton(CONST_ADD_TO_BAG), setAddingSuccess(false);
      }, 3000);
    }
  }, [addingToCart, addingSuccess]);

  useEffect(() => {
    let selectedVariant = null;
    selectedVariant =
      product.variants.find((variant) => {
        return (
          variant.option1 === option1 && variant.option2 === option2 && variant.option3 === option3 && variant.available
        );
      }) || null;

    // Prevent showing unavailable (Non existent) variants.
    if (!selectedVariant) {
      let selectedVariants;
      switch (optionChanged) {
        case 0:
          selectedVariants =
            product.variants.filter((variant) => {
              return variant.option1 === option1 && variant.available;
            }) || selectedVariant;
          selectedVariant =
            selectedVariants.find((variant) => {
              return variant.option2 === option2;
            }) || selectedVariants[0];
          if (selectedVariant) {
            setOption2(selectedVariant.option2);
            setOption3(selectedVariant.option3);
          }
          break;
        case 1:
          selectedVariants = product.variants.filter((variant) => {
            return variant.option2 === option2 && variant.available;
          });
          selectedVariant =
            selectedVariants.find((variant) => {
              return variant.option1 === option1;
            }) || selectedVariants[0];
          if (selectedVariant) {
            setOption1(selectedVariant.option1);
            setOption3(selectedVariant.option3);
          }
          break;
        case 2:
          selectedVariants =
            product.variants.filter((variant) => {
              return variant.option3 === option3 && variant.available;
            }) || selectedVariant;
          selectedVariant =
            selectedVariants.find((variant) => {
              return variant.option2 === option2;
            }) || selectedVariants[0];
          if (selectedVariant) {
            setOption1(selectedVariant.option1);
            setOption2(selectedVariant.option2);
          }
          break;
      }
    }
    setCurrentVariant(selectedVariant);
  }, [option1, option2, option3, optionChanged]);

  // Fire price_drop once per PDP load (product view), intentionally not on variant change.
  useEffect(() => {
    if (currentVariant?.id) {
      Braze.logCatalogSubscription({ id: String(currentVariant.id), type: ["price_drop"] });
    }
  }, []);

  const onSwatchSelected = (option: number, value: string) => {
    // Set the value of the option based on the option number changed. In shopify there are 3 levels of options.
    switch (option) {
      case 0:
        setOption1(value);
        setOptionChanged(0);
        break;
      case 1:
        setOption2(value);
        setOptionChanged(1);
        break;
      case 2:
        setOption3(value);
        setOptionChanged(2);
        break;
    }
  };
  const Swatches = (props: SwatchesProps) => {
    const { option, index } = props;
    const variantOptions = product.variants.map((variant) => {
      return variant.options[index];
    });
    const set = new Set(variantOptions);
    const values = Array.from(set);

    const swatches = values.map((value) => {
      let selected = false;
      let available = false;
      if (
        (index === 0 && option1 === value) ||
        (index === 1 && option2 === value) ||
        (index === 2 && option3 === value)
      ) {
        selected = true;
      }
      const variantFound = product.variants.find((variant) => {
        switch (index) {
          case 0:
            return variant.option1 === value && variant.available;
            break;
          case 1:
            return variant.option1 === option1 && variant.option2 === value && variant.available;
            break;
          case 2:
            return (
              variant.option3 === value &&
              variant.option1 === option1 &&
              variant.option2 === option2 &&
              variant.available
            );
            break;
          default:
            return false;
        }
      });

      if (variantFound) {
        available = true;
      }
      return {
        title: value,
        value: value,
        group: option,
        sku: variantFound ? variantFound.sku : "",
        selected,
        available,
      };
    });
    return (
      <SwatchesGroup
        index={index}
        onSwatchSelected={onSwatchSelected}
        key={option}
        option={option}
        swatches={swatches}
      />
    );
  };
  const Options = () => {
    if (product.options.length > 0) {
      return (
        <>
          {product.options.map((option: string, index: number) => (
            <Swatches key={`swatches-${option}`} option={option} index={index} />
          ))}
        </>
      );
    } else {
      return <></>;
    }
  };
  return (
    <>
      {currentVariant && (
        <>
          <Price
            productId={product.id}
            price={currentVariant.price}
            compareAtPrice={currentVariant.compare_at_price}
            moneyFormat={moneyFormat}
          />
          {afterpayEnabled && <Afterpay />}
        </>
      )}
      <ColourVariations
        productHandle={product.handle}
        colourVariations={colourVariations}
        outOfStockTitle={productSectionSettings.outOfStockTitle}
        outOfStockMessage={productSectionSettings.outOfStockMessage}
      />
      <Options />
      {isGiftCard && <GiftCardForm giftCardData={giftCardData} setGiftCardData={setGiftCardData} />}
      <Button
        disabled={currentVariant && currentVariant.available && !addingToCart && !invalidGiftCardData ? false : true}
        className="button-gold button--block"
        onClick={onAddToBag}
      >
        {currentVariant && currentVariant.available ? addToCartButton : currentVariant ? "Sold out" : "Unavailable"}
      </Button>
    </>
  );
};
export default ProductForm;
